import { loadCloudinaryScript } from "./loadCloudinaryScript";

declare global {
  interface Window {
    cloudinary: {
      openUploadWidget: (options: any, callback: (error: any, result: any) => void) => void;
      galleryWidget: (options: any) => any;
      createMediaLibrary: (options: any) => any;
    };
  }
}

export interface UploadResult {
  secureUrl: string;
  publicId: string;
  width: number;
  height: number;
  format: string;
  bytes: number;
}

export interface UploadOptions {
  cloudName: string;
  uploadPreset: string;
  sources?: string[];
  multiple?: boolean;
  maxFiles?: number;
  cropping?: boolean;
  showSkipCropButton?: boolean;
  croppingAspectRatio?: number;
  publicId?: string;
  folder?: string;
  resourceType?: string;
  showPoweredBy?: boolean;
  clientAllowedFormats?: string[];
  maxFileSize?: number;
}

const uploadImage = async (options: UploadOptions): Promise<UploadResult | UploadResult[]> => {
  try {
    await loadCloudinaryScript();

    // Check if cloudinary is available
    if (!window.cloudinary || !window.cloudinary.openUploadWidget) {
      throw new Error("Cloudinary upload widget not available");
    }

    return new Promise((resolve, reject) => {
      // Convert camelCase to snake_case manually for key options
      const scOptions = {
        cloud_name: options.cloudName,
        upload_preset: options.uploadPreset,
        cropping: options.cropping,
        sources: options.sources,
        multiple: options.multiple,
        max_files: options.maxFiles,
        cropping_aspect_ratio: options.croppingAspectRatio,
        resource_type: options.resourceType,
        show_skip_crop_button: options.showSkipCropButton,
        folder: options.folder,
        show_powered_by: options.showPoweredBy,
        client_allowed_formats: options.clientAllowedFormats,
        public_id: options.publicId
      };

      const uploadResults: UploadResult[] = [];

      window.cloudinary.openUploadWidget(scOptions, (error: any, res: any) => {
        if (!error) {
          if (res.event === "success") {
            // For multiple uploads, Cloudinary calls this callback multiple times
            // Each call contains info for one uploaded file
            const result: UploadResult = {
              secureUrl: res.info.secure_url,
              publicId: res.info.public_id,
              width: res.info.width,
              height: res.info.height,
              format: res.info.format,
              bytes: res.info.bytes
            };

            if (options.multiple) {
              uploadResults.push(result);
              // Don't resolve yet - wait for all uploads to complete
            } else {
              // Single upload - resolve immediately
              resolve(result);
            }
          } else if (res.event === "close") {
            if (options.multiple && uploadResults.length > 0) {
              // Multiple uploads completed - resolve with all results
              resolve(uploadResults);
            } else if (options.multiple && uploadResults.length === 0) {
              // User closed without uploading anything
              reject(new Error("Upload cancelled by user"));
            } else {
              // Single upload cancelled
              reject(new Error("Upload cancelled by user"));
            }
          }
        } else {
          console.error("Cloudinary upload error:", error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error("Error in uploadImage:", error);
    throw error;
  }
};

export default uploadImage;

export const handleImageUpload = async (
  uploadOptions: UploadOptions,
  setFieldValue: (field: string, value: any) => void,
  setUploadError: (error: string | null) => void,
  fieldName: string,
  photos?: any[],
  rank?: number
): Promise<UploadResult | UploadResult[] | null> => {
  try {
    setUploadError(null);
    const result = await uploadImage(uploadOptions);

    // Handle both single and multiple uploads
    if (Array.isArray(result)) {
      // Multiple uploads - use the first one for backward compatibility
      const firstResult = result[0];
      if (photos) {
        if (rank) {
          photos.push({ reference: firstResult.secureUrl, rank });
        } else {
          photos.push(firstResult.secureUrl);
        }
      }
      setFieldValue(fieldName, photos || firstResult.secureUrl);
      return result; // Return the full array
    } else {
      // Single upload
      if (photos) {
        if (rank) {
          photos.push({ reference: result.secureUrl, rank });
        } else {
          photos.push(result.secureUrl);
        }
      }
      setFieldValue(fieldName, photos || result.secureUrl);
      return result;
    }
  } catch (e: any) {
    const errorMessage = e.message || e.statusText || "Upload failed";
    setUploadError(errorMessage);
    return null;
  }
};
